import { useRouter, useRouterState } from '@tanstack/react-router';
import type { NavEntry } from '@/components/shared/Sidebar/types';
import { useCanAccessEntityScreening } from '@/hooks/useCanAccessEntityScreening';
import { useFeatures } from '@/hooks/useFeatures';
import { usePermissions } from '@/hooks/usePermissions';
import { useTranslator } from '@/hooks/useTranslator';
import {
  canImpersonate,
  isRetracedAdmin,
  MANAGEMENT_ROOT,
} from '@/lib/utils/managementAccess';
import { buildManagementNav } from '@/lib/utils/managementNav';
import { hasCompany } from '@/lib/utils/session';
import { buildNav, isPathActive } from '@/lib/utils/sidebarNavHelpers';
import { useSessionStore } from '@/store/sessionStore';

/**
 * Resolves which nav the sidebar should show, so `Sidebar` itself stays a
 * presentation component that renders whatever entries it is given.
 *
 * The app has two navigable areas and they are alternatives, not layers:
 * management swaps the rail's entries rather than appending to them, because
 * nothing in Catalog / Compliance / Reports applies while you are
 * administering the platform. Both are the same `NavEntry[]` shape, so the
 * rail, flyout, pinning, and active matching are one code path.
 *
 * Every hook is read before the branch, so the call order stays stable while
 * only the nav actually needed gets built.
 */
export function useSidebarNav(): NavEntry[] {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const router = useRouter();
  const { getTranslation } = useTranslator({ page: 'sidebar' });
  const features = useFeatures();
  const canAccessEntityScreening = useCanAccessEntityScreening();
  const perms = usePermissions();
  const user = useSessionStore(s => s.user);

  if (isPathActive(pathname, MANAGEMENT_ROOT)) {
    // No feature flags or permissions: the area is already gated on being
    // Retraced staff (see `canAccessManagement`) and these tools are not sold
    // per-company. Role still matters for the two entries the old menu keeps to
    // RETRACED_ADMIN, so that one distinction is threaded through.
    return buildManagementNav(getTranslation, {
      // Not `user.role`: that is the role in the company the request was
      // scoped to, so an admin looking at a customer reads back as whatever
      // their membership there says. See `isRetracedAdmin`.
      isRetracedAdmin: isRetracedAdmin(user),
      canImpersonate: canImpersonate(user),
      // The route table, so the menu lists a tool the day its page lands and
      // never a day earlier. Index routes are registered with and without the
      // trailing slash; either one means the page exists.
      hasRoute: href =>
        href in router.routesByPath || `${href}/` in router.routesByPath,
    });
  }

  const clientNav = buildNav(
    features,
    getTranslation,
    canAccessEntityScreening,
    {
      orders: perms.orders,
      automationRules: perms['automation-rules'],
      risk: perms.risk,
      productMapping: perms['product-mapping'],
      supplyChainMapping: perms['supply-chain-mapping'],
      constantMapping: perms['constant-mapping'],
      apiKey: perms['api-key'],
      entityScreening: perms['entity-screening'],
      reports: perms.reports,
      styles: perms.styles,
      network: perms.network,
      questionnaires: perms.questionnaires,
      documents: perms.documents,
      certificates: perms.certificates,
      capa: perms.capa,
      audit: perms.audit,
      slcp: perms.slcp,
      fiberForward: perms['fiber-forward'],
      files: perms.files,
      certifiedMaterials: perms['certified-materials'],
    },
  );
  // A companyless user has nothing to see in Products/Compliance/Traceability/
  // Risk/Reports/etc. — those are all scoped to a company. Only Home stays.
  return hasCompany(user) ? clientNav : clientNav.slice(0, 1);
}
